import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Bell, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GREEN = '#00FF41';
const GOLD = '#FFD700';
const RED = '#FF3B30';

const sevColor = (s) => (s === 'critical' ? RED : s === 'warning' ? GOLD : GREEN);
const SevIcon = ({ s, color }) => (s === 'critical' ? <AlertCircle className="w-4 h-4" style={{ color }} /> : s === 'warning' ? <AlertTriangle className="w-4 h-4" style={{ color }} /> : <CheckCircle className="w-4 h-4" style={{ color }} />);

/**
 * Slide-out alerts drawer for the God's Eye.
 * GET /admin/alerts + PATCH /admin/alerts/{id}/read
 * Poll every 30s while open; owner shows a bell badge in the OS header.
 */
export default function AdminAlertsDrawer({ open, onOpenChange, adminKey }) {
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchAlerts = async () => {
    if (!adminKey) return;
    try {
      const res = await axios.get(`${API}/admin/alerts?admin_key=${adminKey}`);
      setAlerts(res.data.alerts || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    fetchAlerts();
    const iv = setInterval(fetchAlerts, 30000);
    return () => clearInterval(iv);
  }, [open, adminKey]);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/alerts/${id}/read?admin_key=${adminKey}`);
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { /* ignore */ }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-black border-l-2 text-white overflow-y-auto"
        style={{ borderColor: `${GREEN}66`, boxShadow: `-4px 0 30px ${GREEN}44` }}
        data-testid="admin-alerts-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-black tracking-widest flex items-center gap-2" style={{ fontFamily: 'Orbitron, sans-serif', color: GREEN, textShadow: `0 0 6px ${GREEN}` }}>
            <Bell className="w-4 h-4" /> ALERTS <span className="text-xs text-emerald-300/70">({unread} unread)</span>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-2 mt-4">
          {alerts.length === 0 && (
            <div className="text-center py-8 text-emerald-300/60 text-sm font-mono">
              <CheckCircle className="w-8 h-8 mx-auto mb-2" style={{ color: GREEN }} />
              All systems nominal — no active alerts
            </div>
          )}
          {alerts.map((a) => {
            const color = sevColor(a.severity);
            return (
              <div
                key={a.id}
                data-testid={`alert-${a.id}`}
                className={`rounded-lg border p-3 transition ${a.read ? 'opacity-60' : ''}`}
                style={{ borderColor: `${color}66`, background: a.read ? 'rgba(0,0,0,0.4)' : 'rgba(0,20,5,0.7)', boxShadow: !a.read ? `0 0 8px ${color}33 inset` : 'none' }}
              >
                <div className="flex items-start gap-2">
                  <SevIcon s={a.severity} color={color} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white leading-tight">{a.title}</p>
                    <p className="text-xs text-zinc-400 leading-tight mt-0.5">{a.message}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[9px] font-mono text-zinc-500">{new Date(a.created_at).toLocaleString()}</p>
                      {!a.read && (
                        <button
                          onClick={() => markRead(a.id)}
                          data-testid={`alert-mark-read-${a.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border hover:bg-emerald-900/30 transition"
                          style={{ color: GREEN, borderColor: `${GREEN}66` }}
                        >
                          Mark Read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
